import React from "react";
import { Box, Button, Group, NativeSelect, Text, TextInput } from "@mantine/core";
import { Plus, Trash2 } from "lucide-react";

/** Single ``{start, end, available}`` slot — same shape as
 *  ``Room.metadata.booking_slots`` on the backend and
 *  ``crm.WeeklySchedule`` rows after the slot refactor. */
export interface Slot {
  start: string;
  end: string;
  available: boolean;
}

export interface SlotsEditorProps {
  value: Slot[];
  onChange: (next: Slot[]) => void;
  disabled?: boolean;
  /** Default duration applied when the operator clicks "Add available slot".
   *  ``EditRoomPage`` defaults to ``{08:00, 17:00}``, the tenant form to
   *  the day's existing extents — let the host pass its own. */
  defaultAvailable?: { start: string; end: string };
  /** Default duration for the "Add blocked slot" button. Most use cases
   *  default to a lunch break window. */
  defaultBlocked?: { start: string; end: string };
  /** Rendered in place of the whole row list while ``value`` is empty —
   *  e.g. "No hours set". The two "Add …" buttons still render beneath it,
   *  so the operator can always add a first slot. Has no effect once the
   *  list is non-empty, and never replaces the per-row
   *  "Available / Blocked" select. */
  emptyState?: React.ReactNode;
}

const DEFAULT_AVAILABLE = { start: "08:00", end: "17:00" };
const DEFAULT_BLOCKED = { start: "12:00", end: "13:00" };

/** Shared editor for slot lists. Used by ``EditRoomPage`` for
 *  ``Room.metadata.booking_slots`` and by ``TenantForm`` for each
 *  weekday's ``WeeklySchedule`` rows. Layout and copy match the
 *  meeting-room booking-hours editor so operators see the same UX
 *  whether they're editing a room or a tenant. */
export const SlotsEditor: React.FC<SlotsEditorProps> = ({
  value,
  onChange,
  disabled = false,
  defaultAvailable = DEFAULT_AVAILABLE,
  defaultBlocked = DEFAULT_BLOCKED,
  emptyState,
}) => {
  const replace = (idx: number, patch: Partial<Slot>) => {
    const next = value.slice();
    next[idx] = { ...next[idx], ...patch };
    onChange(next);
  };
  const remove = (idx: number) => {
    const next = value.slice();
    next.splice(idx, 1);
    onChange(next);
  };
  const append = (slot: Slot) => onChange([...(value || []), slot]);

  if (value.length === 0 && emptyState) {
    return (
      <Box>
        {emptyState}
        <Group gap="xs" mt="xs">
          <Button
            variant="outline"
            size="compact-sm"
            leftSection={<Plus size={12} />}
            onClick={() => append({ ...defaultAvailable, available: true })}
            disabled={disabled}
          >
            Add available slot
          </Button>
          <Button
            variant="outline"
            size="compact-sm"
            leftSection={<Plus size={12} />}
            onClick={() => append({ ...defaultBlocked, available: false })}
            disabled={disabled}
          >
            Add blocked slot
          </Button>
        </Group>
      </Box>
    );
  }

  return (
    <Box>
      {value.map((slot, i) => (
        <Group key={i} gap="xs" align="center" mb="xs">
          <NativeSelect
            size="sm"
            value={slot.available ? "available" : "blocked"}
            disabled={disabled}
            onChange={(e) => replace(i, { available: e.currentTarget.value === "available" })}
            data={[
              { value: "available", label: "Available" },
              { value: "blocked", label: "Blocked" },
            ]}
          />
          <TextInput
            type="time"
            w={128}
            value={slot.start}
            disabled={disabled}
            onChange={(e) => replace(i, { start: e.target.value })}
          />
          <Text fz="sm" c="dimmed">to</Text>
          <TextInput
            type="time"
            w={128}
            value={slot.end}
            disabled={disabled}
            onChange={(e) => replace(i, { end: e.target.value })}
          />
          <Button
            variant="subtle"
            size="compact-sm"
            px={6}
            onClick={() => remove(i)}
            disabled={disabled}
            aria-label="Remove slot"
          >
            <Trash2 size={16} color="var(--mantine-color-red-filled)" />
          </Button>
        </Group>
      ))}
      <Group gap="xs" mt="xs">
        <Button
          variant="outline"
          size="compact-sm"
          leftSection={<Plus size={12} />}
          onClick={() => append({ ...defaultAvailable, available: true })}
          disabled={disabled}
        >
          Add available slot
        </Button>
        <Button
          variant="outline"
          size="compact-sm"
          leftSection={<Plus size={12} />}
          onClick={() => append({ ...defaultBlocked, available: false })}
          disabled={disabled}
        >
          Add blocked slot
        </Button>
      </Group>
    </Box>
  );
};
